type AddSearchParams = Record<string, string | number | boolean>;

/**
 * Helper function that can add or delete search parameters from a URL
 *
 * @param options.addParams - An object containing key-value pairs to be added as search parameters.
 * @param options.deleteParams - An array of strings representing the names of search parameters to be deleted.
 * @param options.replaceHistory - A boolean flag indicating whether to replace the browser history.
 */
export const updateUrlSearchParams = ({
	addParams = {},
	deleteParams = [],
	replaceHistory = true,
}: {
	addParams?: AddSearchParams;
	deleteParams?: string[];
	replaceHistory?: boolean;
}) => {
	const currentUrl = new URL(window.location.href);

	deleteParams.forEach((param) => {
		currentUrl.searchParams.delete(param);
	});

	Object.entries(addParams).forEach(([key, value]) => {
		currentUrl.searchParams.set(key, String(value));
	});

	if (replaceHistory) {
		window.history.replaceState({}, document.title, currentUrl.toString());
	} else {
		window.history.pushState({}, document.title, currentUrl.toString());
	}
};

/**
 * Retrieves the value of a specified query parameter from the window URL.
 * If the key is not found, the function returns `undefined`.
 *
 * @param {string} key - The name of the query parameter whose value is to be retrieved.
 */
export const getQueryParamValue = (key: string) => {
	const currentUrl = new URL(window.location.href);
	const value = currentUrl.searchParams.get(key);

	return value ?? undefined;
};

export const safeExternalUrl = (url?: string): string | undefined => {
	if (!url) return undefined;
	try {
		const { protocol } = new URL(url, window.location.origin);
		return protocol === 'http:' || protocol === 'https:' ? url : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Detects the type of document based on the URL extension.
 * @param url The URL of the document.
 * @returns A string indicating the document type ('pdf', 'img', 'other').
 */
export const detectDocumentType = (url: string) => {
	const extension = url.split('.').pop()?.toLowerCase();

	const pdfExtensions = ['pdf'];
	const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg'];

	if (extension && pdfExtensions.includes(extension)) return 'pdf';
	if (extension && imageExtensions.includes(extension)) return 'img';

	return 'other';
};
